import { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import JsonView from "./JsonView";
import { bodyToText, entryToJson, formatBytes, formatDuration, formatClock, shortContentType, statusLabel } from "./netdiagUtils";

const copy = (text, what) => {
    navigator.clipboard?.writeText(text)
        .then(() => toast.success(`${what} copied to clipboard.`, { position: "bottom-center", autoClose: 1200 }))
        .catch(() => toast.error(`Could not copy ${what.toLowerCase()}.`));
};

const Section = ({ title, action, defaultOpen = true, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="netdiag-section">
            <div className="netdiag-section-head">
                <button type="button" className="netdiag-section-toggle" onClick={() => setOpen((prev) => !prev)} aria-expanded={open}>
                    <span className="netdiag-json-caret">{open ? "▾" : "▸"}</span>
                    {title}
                </button>
                {action}
            </div>
            {open && <div className="netdiag-section-body">{children}</div>}
        </div>
    );
};

Section.propTypes = {
    title: PropTypes.node.isRequired,
    action: PropTypes.node,
    defaultOpen: PropTypes.bool,
    children: PropTypes.node,
};

const HeaderTable = ({ headers }) => {
    const rows = Object.entries(headers ?? {});
    if (!rows.length) return <div className="netdiag-empty-note">No headers recorded.</div>;
    return (
        <table className="netdiag-kv">
            <tbody>
                {rows.map(([key, value]) => (
                    <tr key={key}>
                        <th scope="row">{key}</th>
                        <td>{value}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

HeaderTable.propTypes = {
    headers: PropTypes.object,
};

// A captured body: JSON gets the collapsible tree, anything else falls back to raw text.
const Payload = ({ body }) => {
    if (!body) return <div className="netdiag-empty-note">No payload.</div>;
    return (<>
        {body.truncated && (
            <div className="netdiag-warn-note">
                Too large to parse as JSON ({body.length.toLocaleString()} characters); showing the start as text.
            </div>
        )}
        {body.json === null ? <pre className="netdiag-pre">{body.text}</pre> : <JsonView data={body.json} />}
    </>);
};

Payload.propTypes = {
    body: PropTypes.shape({
        text: PropTypes.string,
        json: PropTypes.any,
        truncated: PropTypes.bool,
        length: PropTypes.number,
    }),
};

const NetworkRequestDetail = ({ entry }) => {
    if (!entry) {
        return <div className="netdiag-detail netdiag-detail-empty">Select a request to inspect its headers and payloads.</div>;
    }

    return (
        <div className="netdiag-detail">
            <Section
                title="General"
                action={
                    <button type="button" className="netdiag-link-btn" onClick={() => copy(entryToJson(entry), "Request")}>
                        Copy entry
                    </button>
                }
            >
                <table className="netdiag-kv">
                    <tbody>
                        <tr>
                            <th scope="row">URL</th>
                            <td className="netdiag-url-cell">
                                <span>{entry.url}</span>
                                <button type="button" className="netdiag-link-btn" onClick={() => copy(entry.url, "URL")}>Copy</button>
                            </td>
                        </tr>
                        <tr><th scope="row">Method</th><td>{entry.method}</td></tr>
                        <tr>
                            <th scope="row">Status</th>
                            <td>{statusLabel(entry)}{entry.statusText ? ` ${entry.statusText}` : ""}</td>
                        </tr>
                        {entry.error && <tr><th scope="row">Error</th><td className="netdiag-error-text">{entry.error}</td></tr>}
                        <tr><th scope="row">Started</th><td>{formatClock(entry.startedAt)}</td></tr>
                        <tr><th scope="row">Duration</th><td>{formatDuration(entry.durationMs)}</td></tr>
                        <tr><th scope="row">Response size</th><td>{formatBytes(entry.size)}</td></tr>
                        <tr><th scope="row">Content type</th><td>{shortContentType(entry.contentType)}</td></tr>
                        <tr><th scope="row">Initiator</th><td>{entry.kind === "xhr" ? "XMLHttpRequest" : "fetch"}</td></tr>
                    </tbody>
                </table>
            </Section>

            <Section title="Request headers" defaultOpen={false}>
                <HeaderTable headers={entry.requestHeaders} />
            </Section>

            <Section title="Request payload" defaultOpen={!!entry.request}>
                <Payload body={entry.request} />
            </Section>

            <Section title="Response headers" defaultOpen={false}>
                <HeaderTable headers={entry.responseHeaders} />
            </Section>

            <Section
                title="Response payload"
                action={entry.response
                    ? <button type="button" className="netdiag-link-btn" onClick={() => copy(bodyToText(entry.response), "Response")}>Copy</button>
                    : null}
            >
                {entry.pending
                    ? <div className="netdiag-empty-note">Waiting for the response…</div>
                    : <Payload body={entry.response} />}
            </Section>
        </div>
    );
};

NetworkRequestDetail.propTypes = {
    entry: PropTypes.object,
};

export default NetworkRequestDetail;
